import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Typography } from '@mui/material';

import svgBackground from 'theSource/assets/noBenefitsIconBkg.svg';
import {
  BenefitsCardStyled,
  BenefitCategoriesWrapperStyled,
  FormattedPlanYearStyled,
  NoEnrolledBenefitsBoxStyled,
  NoBenefitsIconBoxStyled,
  StethoscopeIconStyled,
  EyeglassesIconStyled,
  ToothIconStyled,
} from './MyBenefitsStyled';
import {
  BenefitCategorySection,
  PendingChangesSection,
  PerPayPeriodHeader,
} from './components';
import { mapPlanYearDates, mapBenefitsForContext } from './utils';
import { BenefitCategoryDto } from './types';
import {
  useEnrollmentProfileQuery,
  useEnrollmentFamilyQuery,
} from 'enrollment/queries';
import { BackLink, PageContentHeaderDisplay } from 'enrollment/components';
import {
  useConfigurationQuery,
  useEnrollmentContextQuery,
  useBenefitsQuery,
  EnrollmentContext,
  BenefitDto,
} from 'theSource/queries';
import { ROUTES } from 'theSource/app/routes';
import { CoverageButtonStyled } from './components/CoverageCard/CoverageCardStyled';
import { EmailConfirmationModal } from 'components/EmailConfirmationModal';
import { DownloadConfirmation } from 'components/DownloadConfirmation';
import { ThemedHeader } from 'components/ThemedHeader';

export interface BenefitContextState {
  state: {
    context?: string;
    startDate?: string;
    endDate?: string;
  };
}

export const MyBenefits = () => {
  const { t } = useTranslation('pages.benefits');
  const state = useLocation().state as BenefitContextState;
  const contextState =
    state && state.state.context ? t(state.state.context) : t('current');

  const { configuration } = useConfigurationQuery();
  const lumosApiHost = configuration?.apiHost;
  const { enrollmentContext } = useEnrollmentContextQuery({
    enabled: true,
    apiHost: String(lumosApiHost),
  });
  const benefitsData = useBenefitsQuery({ enabled: true });
  const { subscriber } = useEnrollmentProfileQuery({ lumosEnabled: true });
  const { dependentsData } = useEnrollmentFamilyQuery({ lumosEnabled: true });
  const isScreenSmall = window.innerWidth < 744;

  if (!benefitsData || !enrollmentContext) {
    return <></>;
  }

  const benefits = mapBenefitsForContext(benefitsData?.data, contextState);
  const contexts = enrollmentContext?.data as EnrollmentContext[];
  const { startsOn, endsOn } =
    state && state.state.startDate && state.state.endDate
      ? { startsOn: state.state.startDate, endsOn: state.state.endDate }
      : mapPlanYearDates(benefits);
  const startsOnDate = new Date(startsOn);
  const endsOnDate = new Date(endsOn);

  const confirmationStatementProps = {
    enrollmentContext: undefined,
    planYearStartsOn: startsOnDate,
    planYearEndsOn: endsOnDate,
  };

  const categories = [] as BenefitCategoryDto[];
  contexts.forEach((context) => {
    context.included_plan_years?.forEach((planYear) => {
      if (
        new Date(planYear.starts_on).valueOf() === startsOnDate.valueOf() ||
        new Date(planYear.ends_on).valueOf() === endsOnDate.valueOf()
      ) {
        planYear.benefit_categories.forEach((category) => {
          if (!categories.find((c) => c.lookup_code === category.lookup_code)) {
            categories.push(category);
          }
        });
      }
    });
  });

  const formattedStartsOn = startsOnDate.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
  const formattedEndsOn = endsOnDate.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });

  const lifeEventContext = contexts?.find(
    (context: EnrollmentContext) =>
      context.enrollment_type_lookup_code === 'life_event' &&
      context.status !== 'open'
  );
  const lifeEventID = lifeEventContext
    ? lifeEventContext.subscriber_life_event?.id
    : undefined;

  const electedBenefits = [] as BenefitDto[];
  const declinedBenefits = [] as BenefitDto[];
  benefits?.forEach((benefit: BenefitDto) => {
    if (benefit.coverage?.req_coverage_level_lookup_code === 'decline') {
      declinedBenefits.push(benefit);
    } else {
      electedBenefits.push(benefit);
    }
  });

  const totalSubPremium = electedBenefits.reduce((acc, benefit) => {
    let planCost = Number(benefit.coverage?.subscriber_premium) || 0; // Ensure we have a number, defaulting to 0 if not available
    benefit.coverage?.rider_coverages?.forEach((rider) => {
      // add rider values
      planCost += Number(rider.subscriber_premium);
    });
    benefit.coverage?.coverage_lines?.forEach((covLine) => {
      covLine.rider_coverage_lines.forEach((riderLine) => {
        planCost += Number(riderLine.subscriber_premium);
      });
    });
    return acc + planCost;
  }, 0);

  const totalOrgPremium = electedBenefits.reduce((acc, benefit) => {
    return acc + (Number(benefit.coverage?.org_premium) || 0);
  }, 0);

  let formattedPlanYearName = t('myBenefitsDates', {
    context: contextState,
    startsOn: formattedStartsOn,
    endsOn: formattedEndsOn,
  });
  formattedPlanYearName = formattedPlanYearName.replace(/&#x2F;/g, '/');

  const checkBenefitCoverage =
    benefitsData?.data?.data?.current ||
    benefitsData?.data?.data?.next ||
    benefitsData?.data?.data?.previous;

  return (
    <div>
      <BackLink
        underline={false}
        testId="back-button"
        className="my-2_5 mt-n1"
        fixedPath={ROUTES.main.home.path}
      />
      <main id="mainContent">
        <ThemedHeader cssClass="mt-2_5">
          {`My ${contextState} Benefits`}
        </ThemedHeader>

        {checkBenefitCoverage && checkBenefitCoverage?.length > 0 ? (
          <div>
            <PageContentHeaderDisplay
              pageContentLookupCode="sub_main/confirm"
              displayPageHeader={false}
              orgPlanYearId={benefits[0]?.org_plan_year_id}
            />

            {formattedPlanYearName && (
              <FormattedPlanYearStyled
                dangerouslySetInnerHTML={{ __html: formattedPlanYearName }}
              ></FormattedPlanYearStyled>
            )}
            <div className="pb-3 pt-1">
              <div className="body1 semiBold">
                {t('pages.home:receiveConfirmation')}
              </div>
              <div>
                <EmailConfirmationModal {...confirmationStatementProps} />
                <DownloadConfirmation {...confirmationStatementProps} />
              </div>
            </div>
            {lifeEventID && (
              <PendingChangesSection
                lifeEventId={lifeEventID}
                firstName={String(subscriber?.data.first_name.value) || ''}
                startsOnDate={startsOnDate}
                endsOnDate={endsOnDate}
              />
            )}
            <BenefitsCardStyled>
              {!isScreenSmall && (
                <PerPayPeriodHeader
                  totalSubPremium={totalSubPremium}
                  totalOrgPremium={totalOrgPremium}
                  additionalClasses="flex-column-reverse w-100"
                />
              )}
              <BenefitCategoriesWrapperStyled>
                {isScreenSmall && (
                  <CoverageButtonStyled>
                    <PerPayPeriodHeader
                      totalSubPremium={totalSubPremium}
                      totalOrgPremium={totalOrgPremium}
                      additionalClasses="flex-column-reverse w-100"
                    />
                  </CoverageButtonStyled>
                )}
                {categories.map((category, index) => (
                  <BenefitCategorySection
                    key={index}
                    benefits={
                      electedBenefits &&
                      electedBenefits.filter(
                        (benefit) =>
                          benefit.org_benefit_category === category.lookup_code
                      )
                    }
                    category={category.name}
                    subscriberProfile={subscriber}
                    dependents={dependentsData?.data.dependents}
                  />
                ))}
                {declinedBenefits.length > 0 && (
                  <BenefitCategorySection
                    key={categories.length + 1}
                    benefits={declinedBenefits}
                    category={t('declined')}
                  />
                )}
              </BenefitCategoriesWrapperStyled>
            </BenefitsCardStyled>
          </div>
        ) : (
          <NoEnrolledBenefitsBoxStyled className="d-flex flex-column align-items-center justify-content-center">
            <NoBenefitsIconBoxStyled bkgImg={svgBackground}>
              <StethoscopeIconStyled size={24} weight="light" />
              <ToothIconStyled size={24} weight="light" />
              <EyeglassesIconStyled size={24} weight="light" />
            </NoBenefitsIconBoxStyled>
            <Typography variant="body1">{t('noEnrolledBenefits')}</Typography>
          </NoEnrolledBenefitsBoxStyled>
        )}
      </main>
    </div>
  );
};
